import { describeColor } from './color'

/**
 * Fills the Official Certificate of Registration SVG template
 * (public/certificate-template.svg, the original artwork) for one color.
 */

export interface CertificateInput {
  name: string
  hex: string
  /** Registration number; undefined renders "Reserved" (or "Pending" when pending=true). */
  regNum?: number | null
  /** ISO date (YYYY-MM-DD); undefined renders "Reserved". */
  regDate?: string | null
  pending?: boolean
}

export interface CertificateOptions {
  /**
   * Keep the artwork's Adobe Fonts import and prefer Futura PT / ITC Galliard
   * when the kit loads. Off by default: the site ships Jost and EB Garamond.
   */
  typekit?: boolean
  /** Extra CSS (typically @font-face rules with data: URLs) embedded in the SVG. */
  fontCss?: string
}

/** Title sizing for the certificate's big name: 300px, shrinking past 15 chars. */
export function certificateTitleSize(name: string): number {
  const df = 300
  const mn = 15
  const t = name.length < mn ? 0 : name.length - mn
  return Math.floor(df - (t + Math.sqrt(t)) * 5.5)
}

export function formatCertificateDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export function fillCertificate(template: string, input: CertificateInput, opts: CertificateOptions = {}): string {
  const v = describeColor(input.hex)
  const name = esc(input.name)
  const regDate = input.regDate ? formatCertificateDate(input.regDate) : input.pending ? 'Pending' : 'Reserved'
  const regNum =
    input.regNum && input.regNum > 0 ? input.regNum.toLocaleString('en-US') : input.pending ? 'Pending' : 'Reserved'

  let s = template
  // Template placeholders, in template order.
  s = s.replace(/#123456/g, v.hex)
  s = s.replace(/IN_COLOR_HEX/g, v.hex)
  s = s.replace(/IN_COLOR_NAME/g, name)
  s = s.replace(/IN_REG_NUM/g, regNum)
  s = s.replace(/IN_REG_DATE/g, regDate)
  s = s.replace(/IN_COLOR_RGB/g, v.rgb.join(', '))
  s = s.replace(/IN_COLOR_CMYK/g, `${v.cmyk[0]}%, ${v.cmyk[1]}%, ${v.cmyk[2]}%`)
  s = s.replace(/IN_COLOR_HSV/g, `${v.hsv[0]}°, ${v.hsv[1]}%, ${v.hsv[2]}%`)
  s = s.replace(/IN_COLOR_HSL/g, `${v.hsl[0]}°, ${v.hsl[1]}%, ${v.hsl[2]}%`)
  s = s.replace(/IN_COLOR_LAB/g, v.lab.join(', '))
  s = s.replace(/IN_COLOR_XYZ/g, v.xyz.join(', '))
  s = s.replace(/IN_COLOR_LCH/g, v.lch.join(', '))
  s = s.replace(/IN_COLOR_HCG/g, v.hcg.join(', '))
  // Text on the color band takes the readable contrast color.
  s = s.replace(/white/g, v.contrast)
  // Big name shrinks with length; very long names drop the label and the duplicate.
  s = s.replace(/300px/g, `${certificateTitleSize(input.name)}px`)
  if (input.name.length > 25) s = s.replace(/Color Name: /g, '')
  if (input.name.length > 35) s = s.replace(/visible/g, 'hidden')

  // Fonts. The artwork names Adobe fonts and imports a Typekit kit.
  if (opts.typekit) {
    s = s.replace(/font-family:\s*futura-pt,/g, "font-family: futura-pt, 'Jost Variable',")
    s = s.replace(/font-family:\s*itc-galliard,/g, "font-family: itc-galliard, 'EB Garamond',")
  } else {
    s = s.replace(/@import url\('https:\/\/use\.typekit\.net\/[^']+'\);\s*/g, '')
    s = s.replace(/font-family:\s*futura-pt,/g, "font-family: 'Jost Variable', futura-pt,")
    s = s.replace(/font-family:\s*itc-galliard,/g, "font-family: 'EB Garamond', itc-galliard,")
  }
  if (opts.fontCss) s = s.replace('<style>', `<style>${opts.fontCss}`)
  return s
}

/** Width and height declared by the artwork (letter landscape at 300dpi). */
export const CERTIFICATE_SIZE = { width: 3300, height: 2550 }
